import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus, faPhone, faXmark, faTriangleExclamation, faGift, faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────────────────────
//  Referrals — a call queue, deliberately not a table of metrics.
//
//  Every family in this market already has a matchmaker, and after a Meet we ask
//  each guest for that one name. A named matchmaker is a warm introduction with
//  a half-life: the 48-hour rule is the product, so the queue sorts OLDEST
//  FIRST and the overdue rows carry the only red on the screen.
//
//  TWO THINGS THIS SCREEN REFUSES TO DO.
//
//  It does not show the referrer's name unless that guest ticked
//  consentToNameReferrer. Speaking a name on a call that the guest never agreed
//  to can cost them a relationship they have had for twenty years — the server
//  withholds it, and the UI says it is withheld rather than showing a blank.
//
//  It does not offer a reward we cannot pay. Only engagement_credit and
//  comp_seat have rails; kundli_credit has none and is not in the menu. A
//  comp_seat says plainly that redemption is not built yet, because an operator
//  must never tell a guest their seat is free on the strength of a token
//  nothing redeems.
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_MAP = {
  submitted:         { cls: "bg-blue-100 text-blue-700",   label: "To call" },
  contacted:         { cls: "bg-amber-100 text-amber-700", label: "Called" },
  registered:        { cls: "bg-indigo-100 text-indigo-700", label: "Registered" },
  hosted_first_meet: { cls: "bg-green-100 text-green-700", label: "Hosted a Meet" },
  rewarded:          { cls: "bg-gray-100 text-gray-600",   label: "Rewarded" },
  rejected:          { cls: "bg-red-100 text-red-600",     label: "Rejected" },
  duplicate:         { cls: "bg-gray-100 text-gray-500",   label: "Duplicate" },
  unreachable:       { cls: "bg-gray-100 text-gray-500",   label: "Unreachable" },
};
const REL_LABEL = {
  my_matchmaker: "Their own matchmaker",
  known: "Knows her",
  heard_of: "Heard of her",
};
const REWARD_LABEL = {
  engagement_credit: "One extra hello (applies immediately)",
  comp_seat: "Free seat token (redemption not built yet)",
};

const Referrals = () => {
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState({});
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(false);

  const [open, setOpen] = useState(null);      // detail payload
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    const params = { limit: 50 };
    if (stage) params.status = stage;
    if (overdueOnly) params.overdue = "true";
    if (search.trim()) params.search = search.trim();
    try {
      const res = await func.get(urls.LIST_REFERRALS_URL, { params, signal });
      if (res?.status === 200) {
        setRows(res?.data?.referrals || []);
        setFunnel(res?.data?.funnel || {});
      } else toast.error("Error fetching referrals");
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  }, [stage, overdueOnly, search]);

  useEffect(() => {
    const c = new AbortController();
    const t = setTimeout(() => load(c.signal), search ? 300 : 0);
    return () => { clearTimeout(t); c.abort(); };
  }, [load, reload]);

  const openDetail = async (id) => {
    try {
      const res = await func.get(`${urls.GET_REFERRAL_BY_ID_URL}${id}`);
      if (res?.status === 200) { setOpen(res.data); setNote(""); }
      else toast.error("Could not open that referral");
    } catch { toast.error("Network error"); }
  };

  const moveStage = async (status) => {
    if (!open) return;
    setSaving(true);
    try {
      const res = await func.put(`${urls.REFERRAL_STAGE_URL}${open.referral._id}/stage`, {
        status, note: note.trim() || undefined,
      });
      if (res?.status === 200) {
        toast.success(`Moved to ${STAGE_MAP[status]?.label || status}`);
        setOpen((o) => ({ ...o, referral: res.data.referral }));
        setNote("");
        setReload((r) => !r);
      } else toast.error(res?.data?.message || "Could not update");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Network error");
    } finally { setSaving(false); }
  };

  const grant = async (type) => {
    if (!open) return;
    setSaving(true);
    try {
      const res = await func.post(`${urls.REFERRAL_REWARD_URL}${open.referral._id}/reward`, { type });
      if (res?.status === 200) {
        toast.success(res.data?.note || "Reward granted");
        setOpen((o) => ({ ...o, referral: res.data.referral }));
        setReload((r) => !r);
      } else toast.error(res?.data?.message || "Could not grant");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Network error");
    } finally { setSaving(false); }
  };

  const r = open?.referral;

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <FontAwesomeIcon icon={faUserPlus} className="text-amber-600" />
        Referred matchmakers
      </h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">
        Names guests gave us. Oldest first — every one is meant to be called within 48 hours.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {Object.keys(STAGE_MAP).map((k) => (
          funnel[k] ? (
            <button key={k} onClick={() => { setStage(stage === k ? "" : k); setOverdueOnly(false); }}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                stage === k ? "border-amber-500 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-600"}`}>
              {STAGE_MAP[k].label} {funnel[k]}
            </button>
          ) : null
        ))}
        <button onClick={() => { setOverdueOnly(!overdueOnly); setStage(""); }}
          className={`px-3 py-1.5 rounded-full text-xs border ${
            overdueOnly ? "border-red-500 bg-red-50 text-red-600" : "border-gray-200 text-gray-600"}`}>
          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
          Overdue
        </button>
      </div>

      <div className="relative max-w-sm mb-4">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400 text-sm" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, number or city"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500" />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Matchmaker</th>
              <th className="text-left px-4 py-3">Number</th>
              <th className="text-left px-4 py-3">How they know her</th>
              <th className="text-left px-4 py-3">Waiting</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
            {!loading && !rows.length && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">
                Nobody has named a matchmaker yet.
              </td></tr>
            )}
            {!loading && rows.map((row) => {
              const st = STAGE_MAP[row.status] || STAGE_MAP.submitted;
              return (
                <tr key={row._id} className={`hover:bg-gray-50 ${row.overdue ? "bg-red-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{row.matchmakerName}</div>
                    <div className="text-xs text-gray-500">
                      {[row.city, row.community].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <a href={`tel:${row.matchmakerPhone}`} className="text-amber-700 hover:underline">
                      <FontAwesomeIcon icon={faPhone} className="mr-1 text-xs" />
                      {row.matchmakerPhone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {REL_LABEL[row.relationship] || row.relationship}
                    {!row.consentToNameReferrer && (
                      <div className="text-[11px] text-gray-400">do not name the guest</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.waitingHours == null
                      ? <span className="text-gray-400">—</span>
                      : <span className={row.overdue ? "text-red-600 font-medium" : "text-gray-600"}>
                          {row.waitingHours}h
                        </span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openDetail(row._id)}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {r && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-800">{r.matchmakerName}</h2>
              <button onClick={() => setOpen(null)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              <a href={`tel:${r.matchmakerPhone}`} className="text-amber-700">{r.matchmakerPhone}</a>
              {r.city ? ` · ${r.city}` : ""}{r.community ? ` · ${r.community}` : ""}
            </p>

            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-600 space-y-1">
              <div>Relationship: <b>{REL_LABEL[r.relationship] || r.relationship}</b></div>
              {open.referrer?.withheld ? (
                <div className="text-gray-500">
                  The guest did not agree to be named — <b>do not say who suggested her.</b>
                </div>
              ) : (
                <div>Suggested by: <b>{open.referrer?.name || "a guest"}</b></div>
              )}
              {open.bureau && (
                <div className="text-green-700">
                  Registered as <b>{open.bureau.bureauName}</b> ({open.bureau.hostStatus})
                </div>
              )}
            </div>

            <label className="block text-xs text-gray-500 mb-1">Note from the call</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" />

            <div className="flex flex-wrap gap-2 mb-4">
              {["contacted", "registered", "hosted_first_meet", "unreachable", "duplicate", "rejected"].map((s) => (
                <button key={s} disabled={saving} onClick={() => moveStage(s)}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {STAGE_MAP[s].label}
                </button>
              ))}
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faGift} className="text-amber-600" /> Thank the guest
              </h3>
              {r.rewardGranted?.type ? (
                <p className="text-sm text-gray-600">
                  Already rewarded: <b>{REWARD_LABEL[r.rewardGranted.type] || r.rewardGranted.type}</b>
                  {r.compToken && <span className="block text-xs text-gray-400 mt-1 break-all">token {r.compToken}</span>}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {(open.grantable || []).map((t) => (
                    <button key={t} disabled={saving} onClick={() => grant(t)}
                      className="text-left px-3 py-2 text-sm rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                      {REWARD_LABEL[t] || t}
                    </button>
                  ))}
                  <p className="text-[11px] text-gray-400">
                    One reward per referral. A free-seat token is stored but cannot be redeemed at
                    registration yet — do not promise a free seat on it.
                  </p>
                </div>
              )}
            </div>

            {!!(r.notes || []).length && (
              <div className="border-t mt-4 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h3>
                <ul className="space-y-2">
                  {(r.notes || []).slice().reverse().map((n, i) => (
                    <li key={i} className="text-xs text-gray-600">
                      <span className="text-gray-400">{new Date(n.at).toLocaleString()}</span> — {n.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Referrals;
